import type { PullRequestDetail } from "@afar-research/contracts";
import {
  usePullRequestSurfaceStore,
  type PullRequestSurfaceDescriptor,
} from "../pullRequestSurfaceStore";
import { useEnvironments } from "../state/environments";
import { executePullRequestDiscussion } from "../pullRequestDiscussionCommand";
import { DiscussionForm } from "./PullRequestDiscussionForm";

/**
 * Whether this Environment can take a remark on this pull request at all. The composer's
 * trigger asks before it draws itself: a button that opens an empty popover is worse than no
 * button, and the guard has to give the same answer in both places.
 */
export function usePullRequestDiscussionAvailable(
  descriptor: PullRequestSurfaceDescriptor,
): boolean {
  const session = usePullRequestSurfaceStore((state) => state.discussions.get(descriptor.id));
  const { environments } = useEnvironments();
  const environment = environments.find((item) => item.environmentId === descriptor.environmentId);
  return (
    session !== undefined &&
    environment?.serverConfig?.environment.capabilities.pullRequestDiscussion === "discussion-v1"
  );
}

export function PullRequestDiscussion({
  descriptor,
  detail,
  active,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly detail: PullRequestDetail;
  readonly active: boolean;
}) {
  const session = usePullRequestSurfaceStore((state) => state.discussions.get(descriptor.id));
  const { environments } = useEnvironments();
  const environment = environments.find((item) => item.environmentId === descriptor.environmentId);
  if (
    !session ||
    environment?.serverConfig?.environment.capabilities.pullRequestDiscussion !== "discussion-v1"
  )
    return null;
  return (
    <LiveDiscussion
      descriptor={descriptor}
      detail={detail}
      active={active}
      session={session}
      enabled={active && environment.connection.phase === "ready"}
    />
  );
}

function LiveDiscussion({
  descriptor,
  detail,
  active,
  session,
  enabled,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly detail: PullRequestDetail;
  readonly active: boolean;
  readonly session: import("../pullRequestDiscussion").PullRequestDiscussionSession;
  readonly enabled: boolean;
}) {
  return (
    <DiscussionForm
      execute={executePullRequestDiscussion}
      session={session}
      descriptor={descriptor}
      detail={detail}
      enabled={enabled}
    />
  );
}
